import { Upload } from "lucide-react";
import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import SearchImg from "../../assets/images/file-search.png";
import AppButton from "../../components/ui/AppButton";
import { Spinner } from "../../components/ui/common";
import {
  ActiveStatusBadge,
  HealthStatusBadge,
} from "../../components/ui/dashboard/StatusCommon";
import InfoItem from "../../components/ui/Info";
import { buildApiUrl } from "../../lib/utils";
import { useGetVehicleByIdQuery } from "../vehicle/vehicleApiSlice";
import ActiveDispatchView from "./components/ActiveDispatchView";
import CreateDispatchView from "./components/CreateDispatchView";
import DispatchBulkUploadModal from "./components/DispatchBulkUploadModal";
import { usePermissions } from "../../hooks/usePermissions";
import { useGetPendingDispatchQuery } from "./dispatchApiSlice";

const DispatchVehicle = () => {
  const { canCreate } = usePermissions();
  const { id } = useParams();
  const navigate = useNavigate();

  const [showCreateView, setShowCreateView] = useState(false);
  const [showBulkUpload, setShowBulkUpload] = useState(false);

  // Vehicle query
  const { data: vehicleData, isLoading: vehicleLoading } =
    useGetVehicleByIdQuery(id || "", {
      skip: !id,
    });

  // Dispatch queries
  const {
    data: pendingDispatchData,
    isLoading: pendingLoading,
    refetch: refetchPending,
  } = useGetPendingDispatchQuery(id || "", {
    skip: !id,
  });

  // Determine which dispatch to show
  const activeDispatch = pendingDispatchData?.data || null;

  const isLoadingDispatch = pendingLoading;

  const refetchDispatches = () => {
    refetchPending();
  };

  if (!id) {
    return <></>;
  }

  if (vehicleLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Spinner />
      </div>
    );
  }

  const vehicle = vehicleData?.data;

  if (!vehicle) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-red-500">Vehicle not found</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-6 w-full bg-[#F9F9F9] p-4">
      <div className="w-full mx-auto space-y-6">
        {/* Breadcrumb */}
        <div className="text-pColor text-[13px] flex flex-wrap items-center gap-x-4 gap-y-1 bg-white rounded-[10px] p-4">
          <button onClick={() => navigate("/admin/vehicles")}>
            All vehicles
          </button>
          <div>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="12"
              height="24"
              viewBox="0 0 12 24"
            >
              <defs>
                <path
                  id="SVG1pzpbdYY"
                  fill="currentColor"
                  d="m7.588 12.43l-1.061 1.06L.748 7.713a.996.996 0 0 1 0-1.413L6.527.52l1.06 1.06l-5.424 5.425z"
                />
              </defs>
              <use
                fillRule="evenodd"
                href="#SVG1pzpbdYY"
                transform="rotate(-180 5.02 9.505)"
              />
            </svg>
          </div>
          <button onClick={() => navigate(`/admin/vehicles`)}>
            {vehicle?.vehicleName}
          </button>
          <div>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="12"
              height="24"
              viewBox="0 0 12 24"
            >
              <defs>
                <path
                  id="SVG1pzpbdYY"
                  fill="currentColor"
                  d="m7.588 12.43l-1.061 1.06L.748 7.713a.996.996 0 0 1 0-1.413L6.527.52l1.06 1.06l-5.424 5.425z"
                />
              </defs>
              <use
                fillRule="evenodd"
                href="#SVG1pzpbdYY"
                transform="rotate(-180 5.02 9.505)"
              />
            </svg>
          </div>
          <button className="font-semibold text-hColor">Dispatch</button>
        </div>

        {/* Vehicle Info */}
        <div className="flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-12 bg-white rounded-[10px] p-4 text-[18px]">
          <div className="flex items-center space-x-10">
            <img
              src={buildApiUrl(vehicle?.vehicleImageUrl)}
              alt={vehicle?.vehicleName}
              className="w-[8rem] h-[8rem] rounded-[10px] object-cover border border-gray-300 shadow-sm"
            />
          </div>
          <div className="w-full text-sm">
            <div className="flex flex-col gap-6 lg:gap-20 lg:flex-row lg:items-center lg:justify-between">
              {/* Vehicle identity */}
              <div className="flex flex-col">
                <div className="flex items-center gap-2">
                  <h1
                    className="font-semibold text-base whitespace-nowrap overflow-hidden text-ellipsis max-w-[240px] sm:max-w-[320px] lg:max-w-[420px]"
                    title={vehicle.vehicleName}
                  >
                    {vehicle.vehicleName}
                  </h1>

                  <ActiveStatusBadge isActive={vehicle.isActive} />
                </div>
                <p className="text-pColor">{vehicle.vehicleModel}</p>
              </div>

              {/* Vehicle details */}
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-7 gap-x-6 gap-y-4">
                <InfoItem label="Vehicle type" value={vehicle.vehicleType} />
                <InfoItem
                  label="Assigned driver"
                  value={vehicle.currentDriver}
                />
                <InfoItem
                  label="Chassis number"
                  value={vehicle.chassisNumber}
                />
                <InfoItem label="License plate" value={vehicle.licensePlate} />

                <div>
                  <p className="text-xs text-pColor mb-1">Vehicle status</p>
                  <ActiveStatusBadge isActive={vehicle.isActive} />
                </div>

                <div>
                  <p className="text-xs text-pColor mb-1">Health status</p>
                  <HealthStatusBadge healthStatus={vehicle.healthStatus} />
                </div>

                <div>
                  <p className="text-xs text-pColor mb-1">Rep status</p>
                  <span className="inline-block text-xs font-semibold border border-gray-300 px-2 py-0.5 rounded-full">
                    {vehicle.replacementStatus}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Dispatch Section */}
        <div className="flex flex-col space-y-2">
          <div className="flex items-center space-x-2 justify-between text-[14px]">
            <h1>Dispatch</h1>
            <div className="flex items-center gap-3">
              {canCreate("Dispatch") && (
                <AppButton
                  appearance="outline"
                  onClick={() => setShowBulkUpload(true)}
                  className="flex items-center px-4 py-2 rounded-lg h-[38px]"
                >
                  <Upload className="h-4 w-4 mr-2" />
                  Bulk upload
                </AppButton>
              )}
              <button
                onClick={() => navigate(`/admin/dispatch-history/${id}`)}
                className="flex items-center"
              >
                Dispatch history
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="12"
                  height="24"
                  viewBox="0 0 12 24"
                  className="ml-4"
                >
                  <defs>
                    <path
                      id="SVG1pzpbdYY"
                      fill="currentColor"
                      d="m7.588 12.43l-1.061 1.06L.748 7.713a.996.996 0 0 1 0-1.413L6.527.52l1.06 1.06l-5.424 5.425z"
                    />
                  </defs>
                  <use
                    fillRule="evenodd"
                    href="#SVG1pzpbdYY"
                    transform="rotate(-180 5.02 9.505)"
                  />
                </svg>
              </button>
            </div>
          </div>

          {showCreateView ? (
            <CreateDispatchView
              vehicleId={id}
              onCancel={() => setShowCreateView(false)}
              onSuccess={() => {
                setShowCreateView(false);
                refetchDispatches();
              }}
            />
          ) : isLoadingDispatch ? (
            <div className="flex items-center justify-center p-8 bg-white rounded-[10px]">
              <Spinner />
            </div>
          ) : activeDispatch ? (
            <ActiveDispatchView
              dispatch={activeDispatch}
              onUpdate={refetchDispatches}
            />
          ) : (
            <div className="h-[40vh] flex items-center justify-center space-x-12 bg-white rounded-[10px] p-4">
              <div className="flex flex-col items-center space-y-6">
                <img
                  src={SearchImg}
                  alt="File Search"
                  className="w-[110px] h-[90px]"
                />
                <h1 className="text-pColor">
                  You have not added any fines to the system yet
                </h1>
                {canCreate("Dispatch") && (
                  <div className="flex items-center gap-3">
                    <AppButton
                      appearance="outline"
                      onClick={() => setShowBulkUpload(true)}
                      className="flex items-center px-4 py-2 rounded-lg h-[38px]"
                    >
                      <Upload className="h-4 w-4 mr-2" />
                      Bulk upload
                    </AppButton>
                    <button
                      onClick={() => setShowCreateView(true)}
                      className="px-6 py-2 bg-primary text-white rounded-md hover:bg-primary/90 transition-colors text-[15px]"
                    >
                      Create Dispatch
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
      <DispatchBulkUploadModal
        isOpen={showBulkUpload}
        vehicleId={id}
        vehiclePlate={vehicle.licensePlate}
        onClose={() => {
          setShowBulkUpload(false);
          refetchDispatches();
        }}
      />
    </div>
  );
};

export default DispatchVehicle;
